<script setup lang="ts">
interface Course {
  title: string
  duration: string
}

interface SkillBlock {
  title: string
  type: 'RNCP' | 'OXO' | 'Transversal' | 'Primary'
  courses: Course[]
}

interface FormationYear {
  label: string
  yearIndex: number
  skillBlocks: SkillBlock[]
}

const props = defineProps<{
  program: FormationYear[]
}>()

const activeYearIndex = ref(0)
const activeBg = ref<HTMLElement | null>(null)
const tabRefs = ref<HTMLElement[]>([])

// Accordion logic
const openBlocks = ref<number[]>([0]) // First block open by default

const toggleBlock = (index: number) => {
  if (openBlocks.value.includes(index)) {
    openBlocks.value = openBlocks.value.filter(i => i !== index)
  } else {
    // For a traditional accordion (one open at a time), uncomment next line
    // openBlocks.value = [index]
    openBlocks.value.push(index)
  }
}

// Reset open blocks when year changes
watch(activeYearIndex, () => {
  openBlocks.value = [0]
})

const setActiveYear = (index: number) => {
  activeYearIndex.value = index
}

// Transition hooks for smooth height animation
const enter = (el: Element) => {
  const element = el as HTMLElement
  element.style.height = '0'
  element.style.opacity = '0'
  // Trigger reflow
  void element.offsetHeight
  element.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  element.style.height = element.scrollHeight + 'px'
  element.style.opacity = '1'
}

const afterEnter = (el: Element) => {
  const element = el as HTMLElement
  element.style.height = 'auto'
}

const leave = (el: Element) => {
  const element = el as HTMLElement
  element.style.height = element.scrollHeight + 'px'
  element.style.opacity = '1'
  // Trigger reflow
  void element.offsetHeight
  element.style.transition = 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  element.style.height = '0'
  element.style.opacity = '0'
}

// Helper to format block type
const getBlockTypeLabel = (type: string) => {
  if (type === 'RNCP') return 'Compétences RNCP' 
  if (type === 'OXO') return 'Compétences OXO'
  if (type === 'Transversal') return 'Compétences Transverses'
  return 'Compétences Majeures'
}

const getBadgeVariant = (type: string) => {
  if (type === 'RNCP') return 'info'
  if (type === 'OXO') return 'warning'
  return 'success'
}
import SectionHeader from '~/components/ui/SectionHeader.vue'
</script>

<template>
  <section class="py-section-py bg-background relative overflow-hidden transition-colors duration-300">
    <div class="container mx-auto px-container-px relative z-10">
      <div class="mb-section-header-mb">
        <SectionHeader 
            title="Le"
            titleHighlight="Programme"
            subtitle="Un cursus progressif conçu pour vous rendre opérationnel dès la sortie."
        />
      </div>

      <div v-if="program && program.length > 0">
        <!-- Year Tabs -->
        <div class="flex justify-center mb-12">
            <UiTabs 
                :items="program.map(y => y.label)" 
                :model-value="program[activeYearIndex]?.label || ''"
                @update:model-value="(label: string) => setActiveYear(program.findIndex(y => y.label === label))"
            />
        </div>

        <!-- Content (Accordion) -->
        <div class="max-w-4xl mx-auto space-y-4">
            <div v-for="(block, bIndex) in program[activeYearIndex]?.skillBlocks || []" :key="bIndex + '-block'" class="group">
                <button 
                    @click="toggleBlock(bIndex)"
                    class="w-full flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 md:p-8 bg-oxo-card-bg border border-oxo-border rounded-2xl hover:border-oxo-red/50 transition-all duration-500 text-left relative overflow-hidden"
                    :class="{ 'border-oxo-red/30 shadow-lg shadow-oxo-red/5 bg-oxo-card-bg/60': openBlocks.includes(bIndex) }"
                >
                    <!-- Background Glow on Open -->
                    <div v-if="openBlocks.includes(bIndex)" class="absolute inset-0 bg-linear-to-br from-oxo-red/5 to-transparent pointer-events-none"></div>

                    <div class="flex items-center gap-6 relative z-10">
                        <div 
                            class="w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-all duration-500"
                            :class="openBlocks.includes(bIndex) ? 'bg-oxo-red text-white scale-110 shadow-lg shadow-oxo-red/20' : 'bg-background border border-oxo-border text-foreground-dim'"
                        >
                            <i :class="['fas', openBlocks.includes(bIndex) ? 'fa-minus' : 'fa-plus', 'text-sm']" aria-hidden="true"></i>
                        </div>
                        <div>
                            <h3 class="font-display font-bold text-lg md:text-xl text-foreground transition-colors group-hover:text-oxo-red">{{ block.title }}</h3>
                        </div>
                    </div>

                    <div class="flex items-center gap-4 relative z-10">
                        <UiBadge 
                            :variant="getBadgeVariant(block.type)"
                            class="whitespace-nowrap"
                        >
                            {{ getBlockTypeLabel(block.type) }}
                        </UiBadge>
                    </div>
                </button>

                <!-- Accordion Content -->
                <Transition
                    name="expand"
                    @enter="enter"
                    @after-enter="afterEnter"
                    @leave="leave"
                >
                    <div v-if="openBlocks.includes(bIndex)" class="overflow-hidden">
                        <div class="p-8 bg-background/30 border-x border-b border-oxo-border/50 rounded-b-2xl -mt-4 pt-12 space-y-6">
                            <ul class="grid grid-cols-1 md:grid-cols-2 gap-4 list-none p-0">
                                <li v-for="(course, cIndex) in block.courses" :key="cIndex" class="flex items-center justify-between group p-3 rounded-xl hover:bg-white/5 transition-all duration-300 border border-transparent hover:border-white/5">
                                    <div class="flex items-center gap-3">
                                        <div class="w-1.5 h-1.5 rounded-full bg-oxo-red group-hover:scale-150 group-hover:shadow-[0_0_8px_rgba(230,0,0,0.5)] transition-all duration-300"></div>
                                        <span class="text-foreground-dim text-sm font-medium group-hover:text-foreground transition-colors">{{ course.title }}</span>
                                    </div>
                                    <span class="text-[10px] font-mono font-bold text-foreground-dim/40 group-hover:text-oxo-red/60 uppercase tracking-widest transition-colors">{{ course.duration }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
      </div>

      <div v-else class="text-center py-12 border-2 border-dashed border-oxo-border rounded-xl">
        <p class="text-foreground-dim">Programme en cours de mise à jour.</p>
      </div>

    </div>
  </section>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Badge standardization */
:deep(.inline-flex.rounded-full) {
  width: 13rem; /* Same as w-52 approx */
  font-weight: 800; /* Extra bold */
}
</style>
